import { useAppSelector } from "../hooks/redux";
import { routes } from "./public/routes";
import { AppUpdateModal } from "../components/CustomComponents/AppUpdateModal";
import { APP_ENV } from "../constants/constant";
import {
  analyticsController,
  circadiaEngineProvider,
  localStorageProvider,
  timeProvider,
} from "../AppController";
import { AppRouter } from "./appRouter";
import { featureFlag } from "../constants/feature";
import { useNetworkStatus } from "../hooks/network";
import { useStatsTimeout } from "../hooks/useStatsTimeout";
import { useChromeOSPlatform } from "../hooks/useChromeOSPlatform";
// import VersionChecker from "../version";
import { ScreenLoaderWhite } from "../components/CustomComponents/ScreenLoaderWhite";
import { PWAInstallBanner } from "../components/CustomComponents/PWAInstallBanner";
import StarRatingPrompt from "../components/CustomComponents/StarRatingPrompt";
import { useEffect } from "react";
import {
  recordTodayVisit,
  hasManuallyOpenedThisSession,
  hasTriggeredIntercomToday,
  getIntercomDelayForToday,
  getIntercomReasonForToday,
  markIntercomTriggeredToday,
} from "../util/intercomTrigger";
import VitalsRedirectHandler from "../components/VitalsRedirectHandler";
import { useVitalsAIMode } from "../hooks/useVitalsAIMode";
import {
  startSession,
  endSession,
  trackPageView,
  getModuleNameFromRoute,
} from "../util/engagementTracking";

window.chimeAudio = new Audio("/assets/audio/bed_exit_chime.wav");
window.chimeAudio.loop = true;

// Global PWA Install Testing Helper (Development only)
if (import.meta.env.DEV) {
  (window as any).PWA_TEST = {
    showBanner: () => {},
    resetCooldown: () => {
      localStorage.removeItem("pwa-install-banner-shown");
    },
    checkStatus: () => {},
  };
}

const Router = () => {
  const isVitalsAIMode = useVitalsAIMode();

  useEffect(() => {
    if (process.env.NODE_ENV === "production") {
      // Auto-update service workers every 5 minutes
      import("../util/serviceWorkerUpdater").then(({ startAutoUpdate }) => {
        startAutoUpdate(5, false); // Don't auto-reload, be polite
      });

      // Ask user every 15 minutes if updates are available
      const checkInterval = setInterval(() => {
        import("../util/serviceWorkerUpdater").then(
          ({ notifyUserOfUpdates }) => {
            notifyUserOfUpdates();
          }
        );
      }, 15 * 60 * 1000);

      return () => clearInterval(checkInterval);
    }
  }, []);
  useNetworkStatus({
    analyticsController,
    localStorageProvider,
    timeProvider,
  });
  useStatsTimeout();

  // Add ChromeOS platform class for specialized styling
  useChromeOSPlatform();

  const { authState, isAuthorized, layoutState, user } = useAppSelector(
    (state) => {
      return {
        authState: state.auth.authState,
        isAuthorized: state.auth.isAuthorized,
        isPinAuthorized: state.auth.isPinAuthorized,
        layoutState: state.layout.layoutState,
        user: state.auth.user,
      };
    }
  );

  // Intercom progressive auto-trigger: Day 1 = 1 min, Day 2 = 2 min, Day 3 = 3 min
  useEffect(() => {
    if (user && isAuthorized) {
      // Record today's visit
      recordTodayVisit();

      // Check if we should skip auto-trigger
      if (hasManuallyOpenedThisSession()) {
        return;
      }

      if (hasTriggeredIntercomToday()) {
        return;
      }

      const delay = getIntercomDelayForToday();
      if (delay === null) {
        return;
      }

      const timer = setTimeout(() => {
        const currentPath = window.location.hash || window.location.pathname;
        const shouldExclude = currentPath.includes("#/dashboard?patientIds");

        if (!shouldExclude) {
          window.dispatchEvent(
            new CustomEvent("openIntercom", {
              detail: {
                delay: 0,
                reason: getIntercomReasonForToday(),
                excludePaths: ["#/dashboard?patientIds"],
              },
            })
          );
          markIntercomTriggeredToday();
        }
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [user, isAuthorized]);

  // Engagement tracking: Session management
  useEffect(() => {
    if (isAuthorized && authState === "authorized") {
      startSession();

      return () => {
        endSession();
      };
    }
  }, [isAuthorized, authState]);

  // Engagement tracking: Page view tracking
  useEffect(() => {
    if (isAuthorized && authState === "authorized") {
      const trackCurrentPage = () => {
        const route = window.location.hash.slice(1) || "/";
        const moduleName = getModuleNameFromRoute(route);
        trackPageView(moduleName, window.location.hash);
      };

      // Track initial page
      trackCurrentPage();

      // Listen for hash changes
      window.addEventListener("hashchange", trackCurrentPage);

      return () => {
        window.removeEventListener("hashchange", trackCurrentPage);
      };
    }
  }, [isAuthorized, authState]);

  // Trigger PWA detection when authentication state changes (router switching)
  useEffect(() => {
    if (isAuthorized && authState === "authorized") {
      // Small delay to ensure router has switched
      setTimeout(() => {
        // Import and trigger PWA detection
        import("../hooks/usePWAInstall").then(() => {
          // This creates a temporary instance to trigger detection
          const tempDetection = () => {
            const userAgent = navigator.userAgent.toLowerCase();
            const isChromOS =
              userAgent.includes("cros") ||
              userAgent.includes("chromeos") ||
              (userAgent.includes("chrome") &&
                userAgent.includes("linux") &&
                userAgent.includes("armv7l")) ||
              navigator.platform.toLowerCase().includes("cros") ||
              /\bCrOS\b/.test(navigator.userAgent);
            const isWebBrowser = !window.electron && !window.circadia;

            if (isChromOS && isWebBrowser) {
              // Dispatch storage event to potentially trigger Chrome's PWA check
              try {
                window.dispatchEvent(
                  new StorageEvent("storage", {
                    key: "pwa-detection-trigger",
                    newValue: Date.now().toString(),
                    url: window.location.href,
                  })
                );
              } catch {
                // Continue
              }

              // Force service worker message
              if (
                "serviceWorker" in navigator &&
                navigator.serviceWorker.controller
              ) {
                navigator.serviceWorker.controller.postMessage({
                  type: "PWA_DETECTION_TRIGGER",
                  timestamp: Date.now(),
                });
              }
            }
          };

          tempDetection();
        });
      }, 2000); // Wait 2 seconds for router to fully switch
    }
  }, [isAuthorized, authState]);

  const getLayoutToRender = () => {
    const url = window.location.hash.slice(1).split("?")[0];

    if (
      !isAuthorized &&
      (!Object.entries(routes)
        .map(([_key, value]) => value.path as string)
        .includes(url) ||
        url === routes.riskReport.path)
    ) {
      if (url === routes.riskReport.path) {
        localStorageProvider.set(
          "prevUrl",
          window.location.hash.slice(1).split("/public").at(-1)!
        );
      } else {
        localStorageProvider.set("prevUrl", window.location.hash.slice(1));
      }
    }

    if (layoutState === "loading" || authState === "loading") {
      return <ScreenLoaderWhite />;
    }

    return (
      <>
        {/* PWA Install Banner - only show for web platforms, not Electron or mobile apps */}
        {!window.electron && !window.circadia && <PWAInstallBanner />}
        <AppRouter />
      </>
    );
  };

  const showUpdateModal =
    APP_ENV === "prod" &&
    circadiaEngineProvider.plat === "ios" &&
    circadiaEngineProvider.intfv < 4 &&
    featureFlag.appUpdateModal; // feature flag

  return (
    <>
      {/* removing version checker for this release */}
      {/* {window.location.hostname !== "localhost" && <VersionChecker />} */}
      {!isVitalsAIMode && <StarRatingPrompt />}
      <AppUpdateModal visible={showUpdateModal} />
      <VitalsRedirectHandler />
      {getLayoutToRender()}
    </>
  );
};

export default Router;
